import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Signature, ShieldBan, KeyRound, ScrollText, Eye, RotateCcw, Wallet, ArrowRight } from 'lucide-react';
import { fetchReceiptStats, fetchPosture, shortHex } from '@/lib/bindingConsent';
import PostureEditor from '@/components/binding/PostureEditor';
import PrincipalsEditor from '@/components/binding/PrincipalsEditor';
import OperatorChecklist from '@/components/binding/OperatorChecklist';
import { GateProposedNotice } from '@/components/binding/GateProposedNotice';

const GATE_OPEN = false;

export const ACTION_CLASSES = [
  { id: 'C0', name: 'Read', icon: Eye, tone: 'text-gray-300 border-white/10', rule: 'Always allowed', example: 'Read a policy page, compare quotes' },
  { id: 'C1', name: 'Reversible', icon: RotateCcw, tone: 'text-cyan-300 border-cyan-500/30', rule: 'Allowed, logged', example: 'Fill a draft form, add to cart' },
  { id: 'C2', name: 'Financial', icon: Wallet, tone: 'text-amber-300 border-amber-500/30', rule: 'Inside mandate limits only', example: 'Pay within a pre-set spend limit' },
  { id: 'C3', name: 'Legally binding', icon: Signature, tone: 'text-red-300 border-red-500/40', rule: 'Blocked unless a consent receipt exists', example: 'Sign, accept terms, execute a contract' },
];

export const POSTURES = {
  deny: { name: 'Default Deny', icon: ShieldBan, desc: 'The agent never executes a C3 action. It halts and hands the exact document to the human.' },
  consent: { name: 'Consent-Gated', icon: Signature, desc: 'A C3 action executes only against a human-signed consent receipt bound to that exact document hash.' },
  scoped: { name: 'Scoped Delegation', icon: KeyRound, desc: 'A standing human-signed delegation for one named document class, with a cap and an expiry. Every execution is still receipted.' },
};

export const TIER_CEILING = { Bronze: 'deny', Silver: 'consent', Gold: 'consent', Platinum: 'scoped' };

export function useBindingPosture(kypaId) {
  const [posture, setPosture] = useState(null);
  const load = useCallback(() => { if (kypaId) fetchPosture(kypaId).then(setPosture).catch(() => setPosture(null)); }, [kypaId]);
  useEffect(load, [load]);
  return [posture, load];
}

export function BindingAuthorityBadge({ posture }) {
  const p = POSTURES[posture?.effective || 'deny'];
  const Icon = p.icon;
  return (
    <Link
      to="/binding-gate"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-xs font-bold uppercase tracking-widest text-red-300 hover:bg-red-500/20 transition-colors"
      data-testid="kypa-agent-binding-badge"
      title="Design preview. Not enforced on-chain."
    >
      <Icon className="w-3.5 h-3.5" /> Binding: {p.name}{' '}
      <span className="ml-1 rounded bg-white/10 px-1 py-px text-[9px] text-gray-300">Preview</span>
    </Link>
  );
}

export function BindingAuthorityCard({ tier, kypaId, wallet, posture, onPostureSaved }) {
  const [listVersion, setListVersion] = useState(0);
  const ceiling = POSTURES[TIER_CEILING[tier] || 'deny'];
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => { fetchReceiptStats(kypaId).then(setStats).catch(() => setStats(null)); }, [kypaId]);
  const current = POSTURES[posture?.effective || 'deny'];
  const signed = posture?.source === 'signed';
  return (
    <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-6" data-testid="kypa-agent-binding-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Signature className="w-4 h-4 text-red-300" />
          <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">Binding authority</h2>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 border border-white/10 rounded px-1.5 py-0.5">Design preview</span>
      </div>
      <div className="space-y-3 text-sm">
        <Row label="Current posture" value={current.name} testid="kypa-agent-binding-posture" cls="text-red-300" />
        <Row label="Set by" value={signed ? 'Operator signature' : 'Default'} testid="kypa-agent-binding-source" cls="text-white" />
        {signed && <Row label="Signature fingerprint" value={shortHex(posture.fingerprint)} testid="kypa-agent-binding-fingerprint" cls="text-white" />}
        {signed && <Row label="Signed" value={new Date(posture.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} testid="kypa-agent-binding-signed-at" cls="text-white" />}
        {posture?.posture === 'scoped' && <Row label="Document type" value={posture.document_class} testid="kypa-agent-binding-doc-class" cls="text-white" />}
        {posture?.posture === 'scoped' && <Row label="Delegation expires" value={new Date(posture.expiry * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })} testid="kypa-agent-binding-expiry" cls={posture.expired ? 'text-amber-300' : 'text-white'} />}
        <Row label={`${tier} tier ceiling`} value={ceiling.name} testid="kypa-agent-binding-ceiling" cls="text-white" />
        <Row label="Consent receipts on record" value={stats ? String(stats.receipt_count) : '...'} testid="kypa-agent-binding-receipts" cls="text-white" />
        <Row label="Executed through the gate" value={stats ? String(stats.consumed_count) : '...'} testid="kypa-agent-binding-executed" cls="text-white" />
      </div>
      {posture?.expired && <p className="text-[11px] text-amber-300 mt-3" data-testid="kypa-agent-binding-expired">Scoped delegation expired. Effective posture is back to Default Deny.</p>}
      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
        Every agent starts at Default Deny. The operator can opt up to the tier ceiling by signing with the agent's registered wallet. Proposed design, not in service. Postures are not being accepted or enforced.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        {GATE_OPEN && <button type="button" onClick={() => setEditing((v) => !v)} className="text-xs font-semibold text-white border border-white/15 rounded-full px-3 py-1.5 hover:border-white/40 transition-colors" data-testid="kypa-agent-binding-change">
          {editing ? 'Close' : 'Change setting'}
        </button>}
        <Link to="/binding-gate" className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-300 hover:text-red-200" data-testid="kypa-agent-binding-demo-link">
          See the proposed design <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {GATE_OPEN && editing && <PostureEditor kypaId={kypaId} wallet={wallet} tier={tier} onSaved={onPostureSaved} />}
      {GATE_OPEN && <OperatorChecklist kypaId={kypaId} wallet={wallet} refreshKey={`${posture?.fingerprint}-${listVersion}`} />}
      {GATE_OPEN && <PrincipalsEditor kypaId={kypaId} wallet={wallet} onSaved={() => setListVersion((v) => v + 1)} />}
      {!GATE_OPEN && <div className="mt-6"><GateProposedNotice testid="kypa-gate-proposed-notice" compact /></div>}
    </div>
  );
}

function Row({ label, value, testid, cls }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{label}</span>
      <span className={`font-mono ${cls}`} data-testid={testid}>{value}</span>
    </div>
  );
}

export function ActionClassGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="binding-action-classes">
      {ACTION_CLASSES.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            viewport={{ once: true }}
            className={`rounded-2xl border bg-white/[0.02] p-5 ${c.tone}`}
            data-testid={`binding-class-${c.id.toLowerCase()}`}
          >
            <div className="flex items-center justify-between mb-3">
              <Icon className="w-5 h-5" />
              <span className="font-mono text-xs font-bold">{c.id}</span>
            </div>
            <h3 className="text-base font-semibold text-white mb-1">{c.name}</h3>
            <p className="text-xs text-gray-400 mb-3">{c.example}</p>
            <p className="text-xs font-semibold">{c.rule}</p>
          </motion.div>
        );
      })}
    </div>
  );
}

export function BindingGateExplainer() {
  return (
    <section className="py-16 px-6" data-testid="kypa-binding-section">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-4" data-testid="kypa-binding-badge">
            <ScrollText className="w-3.5 h-3.5" /> Binding Authority / Proposed
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4" data-testid="kypa-binding-heading">
            A prompt is a suggestion. A gate is a rule.
          </h2>
          <p className="text-base text-gray-400 leading-relaxed">
            Agents can now click, sign, and accept terms on a user's behalf. A rule written into an agent's instructions can be overridden by the agent itself or by text injected from a web page. The proposed Binding Action Gate would move the rule out of the model: legally binding actions blocked by default and executed only against a human-signed consent receipt.
          </p>
        </div>
        <ActionClassGrid />
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kypa-binding-postures">
          {Object.entries(POSTURES).map(([k, p]) => {
            const Icon = p.icon;
            return (
              <div key={k} className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`kypa-binding-posture-${k}`}>
                <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-red-300" /><span className="font-semibold text-white">{p.name}</span></div>
                <p className="text-sm text-gray-400 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-8 rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-500/10 to-transparent p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-testid="kypa-binding-trigger4">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-300 font-mono mb-2">Binding Action Gate</div>
            <p className="text-sm text-gray-200 leading-relaxed">
              As designed, binding actions routed through the gate would execute only against a human-signed consent receipt for the exact document. Proposed design, not in service. Postures are not being accepted or enforced.
            </p>
          </div>
          <Link to="/binding-gate" data-testid="kypa-binding-demo-cta" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors">
            See the proposed design <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
